import { Box, Grid, Stack, TextField, Typography } from '@mui/material';
import { ICaseDetailsInputs } from 'API';
import {
  ControllerDatePicker,
  CustomCheckbox,
  InfoText,
  PaperCard,
  RadioButtons,
  TextareaCounter,
} from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useContext, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { generateError } from 'Regex';
import { IndexContext } from '../CaseContext';
import { SummaryCollapseBox } from './SummaryCollapseBox';
import { useDictionary, useIsDesktop } from 'Hooks';
import { validateLettersAndNumbersOnly } from 'Utils/validateLettersAndNumbersOnly';
import { yesNoOptions } from 'Constants';
import { validateLettersNumbersAndSpacialChars } from 'Utils/validateLettersNumbersAndSpacialChars';
import { styleTextField } from 'Theme';
import { textFieldShrink } from 'Utils';

interface IMedicalFacilityCard {
  deleteFacility: () => void;
  hospitalizationId: number;
  index: number;
  isOpen: boolean;
  setCurrentOpenInjuryDetailCard: Function;
}
export const MedicalFacilityCard = (props: IMedicalFacilityCard) => {
  const { index, baseName = '' } = useContext(IndexContext);

  const {
    register,
    formState: { errors },
    clearErrors,
    setValue,
  } = useFormContext<ICaseDetailsInputs>();

  const [textAreaDescription, setTextAreaDescription] = useState<string>('');

  const dictionary = useDictionary(Dictionary, DICTIONARY.CASE_DETAILS);

  const hospitalBaseName = `${baseName}.${index}`;

  const hospitalizationName = `${hospitalBaseName}.wasHospitalized`;

  const hospitalizationErrorsArray = (() => {
    const caseDetailsIndex = (baseName.split('.')[1] as unknown as number) || 0;
    if (
      errors.caseDetails &&
      errors.caseDetails[caseDetailsIndex] &&
      errors.caseDetails[caseDetailsIndex].hospitalizationDetailsSection
    ) {
      return errors.caseDetails[caseDetailsIndex].hospitalizationDetailsSection;
    } else {
      return undefined;
    }
  })();

  const hospitalizationError =
    hospitalizationErrorsArray !== undefined
      ? hospitalizationErrorsArray[index]
      : undefined;

  const hospitalization = useWatch({
    name: hospitalizationName,
    defaultValue: false,
  }) as boolean;

  const [
    hospitalizationStartDate,
    hasNoClinicDepartment,
    hasNoDoctorName,
    hasNoHospitalizationStartDate,
    hasNoHospitalizationEndDate,
    description,
    medicalInstitutionName,
    clinicDepartment,
    doctorName,
  ] = useWatch({
    name: [
      `${hospitalBaseName}.hospitalizationStartDate`,
      `${hospitalBaseName}.hasNoClinicDepartment`,
      `${hospitalBaseName}.hasNoDoctorName`,
      `${hospitalBaseName}.hasNoHospitalizationStartDate`,
      `${hospitalBaseName}.hasNoHospitalizationEndDate`,
      `${hospitalBaseName}.description`,
      `${hospitalBaseName}.medicalInstitutionName`,
      `${hospitalBaseName}.clinicDepartment`,
      `${hospitalBaseName}.doctorName`,
    ],
  });

  useEffect(() => {
    if (description) setTextAreaDescription(description);
  }, [description]);

  useEffect(() => {
    if (medicalInstitutionName?.length > 0)
      clearErrors(
        `${hospitalBaseName}.medicalInstitutionName` as `caseDetails.0.hospitalizationDetailsSection.0.medicalInstitutionName`
      );
    if (hasNoClinicDepartment) {
      setValue(
        `${hospitalBaseName}.clinicDepartment` as `caseDetails.0.hospitalizationDetailsSection.0.clinicDepartment`,
        ''
      );
      clearErrors(
        `${hospitalBaseName}.clinicDepartment` as `caseDetails.0.hospitalizationDetailsSection.0.clinicDepartment`
      );
    }
    if (hasNoHospitalizationStartDate) {
      setValue(
        `${hospitalBaseName}.hospitalizationStartDate` as `caseDetails.0.hospitalizationDetailsSection.0.hospitalizationStartDate`,
        null
      );
      clearErrors(
        `${hospitalBaseName}.hospitalizationStartDate` as `caseDetails.0.hospitalizationDetailsSection.0.hospitalizationStartDate`
      );
    }
    if (hasNoHospitalizationEndDate) {
      setValue(
        `${hospitalBaseName}.hospitalizationEndDate` as `caseDetails.0.hospitalizationDetailsSection.0.hospitalizationEndDate`,
        null
      );
      clearErrors(
        `${hospitalBaseName}.hospitalizationEndDate` as `caseDetails.0.hospitalizationDetailsSection.0.hospitalizationEndDate`
      );
    }
    if (hasNoDoctorName) {
      setValue(
        `${hospitalBaseName}.doctorName` as `caseDetails.0.hospitalizationDetailsSection.0.doctorName`,
        ''
      );
      clearErrors(
        `${hospitalBaseName}.doctorName` as `caseDetails.0.hospitalizationDetailsSection.0.doctorName`
      );
    }
    if (description)
      clearErrors(
        `${hospitalBaseName}.description` as `caseDetails.0.hospitalizationDetailsSection.0.description`
      );
  }, [
    clearErrors,
    setValue,
    hospitalBaseName,
    hasNoHospitalizationStartDate,
    hasNoClinicDepartment,
    hasNoHospitalizationEndDate,
    hasNoDoctorName,
    medicalInstitutionName,
    description,
  ]);

  return (
    <PaperCard
      index={props.index}
      remove={props.deleteFacility}
      text={<SummaryCollapseBox />}
      isOpen={props.isOpen}
      setCurrentOpenCard={props.setCurrentOpenInjuryDetailCard}
    >
      <Grid
        container
        gap={4}
        sx={{
          '& .MuiFormHelperText-root': { backgroundColor: '#F8F8F8', m: 0 },
        }}
      >
        <div>
          <Typography
            color='primary'
            sx={
              useIsDesktop()
                ? { fontWeight: 'bold' }
                : { fontWeight: 'bold', pl: '40px', pt: '8px' }
            }
          >
            {dictionary.seventhSectionFacilityTitleNew}
          </Typography>
        </div>
        <TextField
          role={
            Boolean(hospitalizationError?.medicalInstitutionName)
              ? 'alert'
              : undefined
          }
          inputProps={{
            maxLength: '50',
          }}
          id={`${dictionary.seventhSectionFacilityFirstInput}-${index}`}
          error={Boolean(hospitalizationError?.medicalInstitutionName)}
          helperText={
            Boolean(hospitalizationError?.medicalInstitutionName?.message) &&
            generateError(
              dictionary.seventhSectionFacilityFirstInput,
              hospitalizationError?.medicalInstitutionName?.message!
            )
          }
          {...register(
            `${hospitalBaseName}.medicalInstitutionName` as `caseDetails.0.hospitalizationDetailsSection.0.medicalInstitutionName`
          )}
          label={dictionary.seventhSectionFacilityFirstInput}
          onChange={(event: any) => validateLettersAndNumbersOnly(event)}
          sx={styleTextField}
        />
        <Stack gap='7px'>
          <ControllerDatePicker
            name={`${hospitalBaseName}.hospitalizationStartDate` as const}
            label={dictionary.seventhSectionFacilitySecondInput}
            disabled={hasNoHospitalizationStartDate}
          />
          <CustomCheckbox
            name={
              `${hospitalBaseName}.hasNoHospitalizationStartDate` as `caseDetails.0.hospitalizationDetailsSection.0.hasNoHospitalizationStartDate`
            }
            label={dictionary.noInformationOrRemember}
          />
        </Stack>
        <RadioButtons
          name={`${hospitalizationName}` as const}
          options={yesNoOptions}
          id={dictionary.seventhSectionFacilityToggleButtonLabel}
        />
        {hospitalization && (
          <>
            <InfoText text={dictionary.differentHospitalDate} />
            <Stack gap='10px'>
              <Stack gap='7px'>
                <TextField
                  role={
                    Boolean(hospitalizationError?.clinicDepartment)
                      ? 'alert'
                      : undefined
                  }
                  inputProps={{
                    maxLength: '50',
                  }}
                  id={`${dictionary.seventhSectionFacilityThirdInput}-${index}`}
                  error={Boolean(hospitalizationError?.clinicDepartment)}
                  helperText={
                    Boolean(hospitalizationError?.clinicDepartment?.message) &&
                    generateError(
                      dictionary.seventhSectionFacilityThirdInput,
                      hospitalizationError?.clinicDepartment?.message!
                    )
                  }
                  {...register(
                    `${hospitalBaseName}.clinicDepartment` as `caseDetails.0.hospitalizationDetailsSection.0.clinicDepartment`
                  )}
                  {...textFieldShrink(clinicDepartment)}
                  label={dictionary.seventhSectionFacilityThirdInput}
                  disabled={hasNoClinicDepartment}
                  sx={styleTextField}
                />
                <CustomCheckbox
                  name={
                    `${hospitalBaseName}.hasNoClinicDepartment` as `caseDetails.0.hospitalizationDetailsSection.0.hasNoClinicDepartment`
                  }
                  label={dictionary.noInformationOrRemember}
                />
              </Stack>
              <Stack gap='7px'>
                <TextField
                  role={
                    Boolean(hospitalizationError?.doctorName)
                      ? 'alert'
                      : undefined
                  }
                  inputProps={{
                    maxLength: '50',
                  }}
                  id={`${dictionary.seventhSectionFacilitySixthInput}-${index}`}
                  error={Boolean(hospitalizationError?.doctorName)}
                  helperText={
                    Boolean(hospitalizationError?.doctorName?.message) &&
                    generateError(
                      dictionary.seventhSectionFacilitySixthInput,
                      hospitalizationError?.doctorName?.message!
                    )
                  }
                  {...register(
                    `${hospitalBaseName}.doctorName` as `caseDetails.0.hospitalizationDetailsSection.0.doctorName`
                  )}
                  {...textFieldShrink(doctorName)}
                  label={dictionary.seventhSectionFacilitySixthInput}
                  disabled={hasNoDoctorName}
                  sx={styleTextField}
                />
                <CustomCheckbox
                  name={
                    `${hospitalBaseName}.hasNoDoctorName` as `caseDetails.0.hospitalizationDetailsSection.0.hasNoDoctorName`
                  }
                  label={dictionary.noInformationOrRemember}
                />
              </Stack>
              <Stack gap='7px'>
                <ControllerDatePicker
                  name={`${hospitalBaseName}.hospitalizationEndDate` as const}
                  label={dictionary.seventhSectionFacilityFifthInput}
                  minDate={hospitalizationStartDate}
                  disabled={hasNoHospitalizationEndDate}
                />
                <CustomCheckbox
                  name={
                    `${hospitalBaseName}.hasNoHospitalizationEndDate` as `caseDetails.0.hospitalizationDetailsSection.0.hasNoHospitalizationEndDate`
                  }
                  label={dictionary.noInformationOrRemember}
                />
              </Stack>

              <Box
                sx={{
                  width: '100%',
                  position: 'relative',
                }}
              >
                <TextField
                  id={`${dictionary.fifthSectionTreatmentDesc}-${index}`}
                  label={dictionary.fifthSectionTreatmentDesc}
                  sx={{ mt: '20px' }}
                  inputProps={{
                    maxLength: '500',
                  }}
                  {...register(
                    `${hospitalBaseName}.description` as `caseDetails.0.hospitalizationDetailsSection.0.description`
                  )}
                  multiline
                  rows={6}
                  onChange={(event: any) => {
                    setTextAreaDescription(event.target.value);
                    validateLettersNumbersAndSpacialChars(event);
                  }}
                />
                <TextareaCounter
                  counter={textAreaDescription.length}
                  limit={500}
                  hasError={false}
                />
              </Box>
            </Stack>
          </>
        )}
      </Grid>
    </PaperCard>
  );
};
